export type LogLevel = "DEBUG" | "INFO" | "WARN" | "ERROR";

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  source: string;
  message: string;
}

const LOG_BUFFER: LogEntry[] = [];
const MAX_BUFFER = 20;
const FLUSH_INTERVAL_MS = 2000;
const LOG_ENDPOINT = "/api/log";

/**
 * `/api/log` só existe no middleware do webpack-dev-server (ver `webpack.config.js`);
 * o host de produção serve arquivos estáticos e não tem esse endpoint. Em produção,
 * o envio remoto é pulado — as entradas já foram impressas no console por `log()`.
 */
const IS_DEV_LOG_ENDPOINT_AVAILABLE = process.env.NODE_ENV !== "production";

let flushTimer: ReturnType<typeof setInterval> | null = null;
let enabled = true;

const LEVEL_ORDER: Record<LogLevel, number> = { DEBUG: 10, INFO: 20, WARN: 30, ERROR: 40 };
const LOG_LEVEL_STORAGE_KEY = "sig:logLevel";

/**
 * Lê o nível mínimo salvo em `localStorage`, permitindo elevar a verbosidade em
 * produção sem novo deploy (`localStorage.setItem("sig:logLevel", "DEBUG")`).
 * Em runtimes de launch event o `localStorage` pode não existir — daí o try/catch.
 */
function readStoredLevel(): LogLevel | null {
  try {
    const raw = localStorage.getItem(LOG_LEVEL_STORAGE_KEY);
    return raw !== null && raw in LEVEL_ORDER ? (raw as LogLevel) : null;
  } catch {
    return null;
  }
}

let minLevel: LogLevel =
  readStoredLevel() ?? (process.env.NODE_ENV === "production" ? "WARN" : "DEBUG");

/**
 * Converte um objeto `Date` para string ISO com precisão de milissegundos,
 * ajustada ao fuso local para facilitar a leitura.
 *
 * @param date - Data a ser formatada.
 * @returns String ISO formatada.
 */
function formatTimestamp(date: Date): string {
  return date.toISOString();
}

/**
 * Envia um lote de logs para o servidor de desenvolvimento.
 * Usa `sendBeacon` como método preferencial (garante entrega mesmo após
 * o descarregamento da página em runtimes efêmeros como launch events).
 * Fallback encadeado: `sendBeacon` → `fetch` (keepalive) → `XMLHttpRequest` → `console.log`.
 *
 * @param entries - Array de entradas de log a serem enviadas.
 */
function sendBatch(entries: LogEntry[]): void {
  if (!enabled || entries.length === 0) return;
  if (!IS_DEV_LOG_ENDPOINT_AVAILABLE) return;

  const body = JSON.stringify(entries);
  const url = LOG_ENDPOINT;

  const doFallback = () => {
    entries.forEach((e) => {
      const fn = e.level === "ERROR" ? console.error
        : e.level === "WARN" ? console.warn
        : console.log;
      fn(`[${e.source}] ${e.message}`);
    });
  };

  try {
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      const sent = navigator.sendBeacon(url, blob);
      if (sent) return;
    }

    if (typeof fetch !== "undefined") {
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
        signal: AbortSignal.timeout?.(3000),
      }).catch(() => doFallback());
    } else if (typeof XMLHttpRequest !== "undefined") {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url);
      xhr.setRequestHeader("Content-Type", "application/json");
      xhr.timeout = 3000;
      xhr.onerror = () => doFallback();
      xhr.ontimeout = () => doFallback();
      xhr.send(body);
    } else {
      doFallback();
    }
  } catch {
    doFallback();
  }
}

/**
 * Descarrega o buffer de logs para o servidor.
 * Chamada automaticamente pelo timer de flush.
 */
function flush(): void {
  if (LOG_BUFFER.length === 0) return;
  const batch = LOG_BUFFER.splice(0, LOG_BUFFER.length);
  sendBatch(batch);
}

/**
 * Inicia o timer de flush automático, se ainda não estiver rodando.
 */
function ensureTimer(): void {
  if (flushTimer !== null) return;
  flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);
}

/**
 * Adiciona uma entrada de log ao buffer e agenda envio.
 *
 * @param level - Nível do log (`DEBUG`, `INFO`, `WARN`, `ERROR`).
 * @param source - Identificador do módulo/função de origem (ex: `"launchevent"`, `"signature-inserter"`).
 * @param message - Mensagem descritiva.
 *
 * @example
 * log("INFO", "launchevent", "Handler registrado.");
 */
export function log(level: LogLevel, source: string, message: string): void {
  if (!enabled) return;
  if (LEVEL_ORDER[level] < LEVEL_ORDER[minLevel]) return;

  const entry: LogEntry = {
    timestamp: formatTimestamp(new Date()),
    level,
    source,
    message,
  };

  // Sempre imprime no console para debugging local rápido
  const fn = level === "ERROR" ? console.error
    : level === "WARN" ? console.warn
    : console.log;
  fn(`[${source}] ${message}`);

  LOG_BUFFER.push(entry);
  ensureTimer();

  // Flush imediato se atingir o limite do buffer
  if (LOG_BUFFER.length >= MAX_BUFFER) {
    flush();
  }
}

/**
 * Log de depuração — detalhes internos.
 *
 * @param source - Identificador do módulo/função de origem.
 * @param message - Mensagem descritiva.
 *
 * @example
 * debug("launchevent", "Template resolvido: template_1.");
 */
export function debug(source: string, message: string): void {
  log("DEBUG", source, message);
}

/**
 * Log informativo — fluxo normal esperado.
 *
 * @param source - Identificador do módulo/função de origem.
 * @param message - Mensagem descritiva.
 *
 * @example
 * info("launchevent", "checkSignature registrado.");
 */
export function info(source: string, message: string): void {
  log("INFO", source, message);
}

/**
 * Log de aviso — algo inesperado mas não crítico.
 *
 * @param source - Identificador do módulo/função de origem.
 * @param message - Mensagem descritiva.
 *
 * @example
 * warn("launchevent", "Template não encontrado — usando fallback.");
 */
export function warn(source: string, message: string): void {
  log("WARN", source, message);
}

/**
 * Log de erro — falha que impede o funcionamento.
 *
 * @param source - Identificador do módulo/função de origem.
 * @param message - Mensagem descritiva.
 *
 * @example
 * error("launchevent", "mailbox.item indisponível.");
 */
export function error(source: string, message: string): void {
  log("ERROR", source, message);
}

/**
 * Força o envio imediato de todos os logs pendentes.
 * Útil para garantir que logs sejam persistidos antes de o runtime ser descarregado.
 *
 * @example
 * ```typescript
 * event.completed();
 * flushNow(); // garante que logs cheguem ao servidor
 * ```
 */
export function flushNow(): void {
  flush();
}

/**
 * Define o nível mínimo de log. Entradas abaixo dele são descartadas —
 * não vão para o console nem para o buffer de envio remoto.
 *
 * A escolha é persistida em `localStorage`, então vale para as próximas sessões.
 *
 * @param level - Nível mínimo (`DEBUG`, `INFO`, `WARN`, `ERROR`).
 *
 * @example
 * setLevel("WARN"); // silencia o fluxo normal, mantém avisos e erros
 */
export function setLevel(level: LogLevel): void {
  minLevel = level;
  try {
    localStorage.setItem(LOG_LEVEL_STORAGE_KEY, level);
  } catch {
    // runtime sem localStorage — nível vale só para a sessão atual
  }
}

/**
 * Nível mínimo atualmente em vigor.
 *
 * @returns O `LogLevel` abaixo do qual as entradas são descartadas.
 */
export function getLevel(): LogLevel {
  return minLevel;
}

/**
 * Habilita ou desabilita o logger por completo (console e envio remoto).
 *
 * @param state - `true` para habilitar, `false` para desabilitar.
 *
 * @example
 * setEnabled(false);
 */
export function setEnabled(state: boolean): void {
  enabled = state;
  if (!state && flushTimer !== null) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
}
